import { useState } from 'react';

import {
  AttachmentOutcome,
  MailMessageStatus,
  type MailAttachmentResponse,
  type MailMessageResponse,
  type MailboxSenderResponse,
} from '../../api';
import {
  AsyncSection,
  DataTable,
  ErrorState,
  PageHeader,
  Panel,
  TextField,
  useNotifications,
  type Column,
} from '../../components';
import { formatDateTime, formatInteger } from '../../format';
import {
  useDiscardMailMessage,
  useMailMessage,
  useMailMessages,
  useMailboxSenders,
  usePollMailbox,
  useRetryMailMessage,
  useRevokeMailboxSender,
} from '../queries';
import {
  ATTACHMENT_OUTCOME_LABELS,
  MAILBOX_COPY,
  MAIL_STATUS_LABELS,
  isOutstanding,
  refusalText,
} from './mailbox';

/**
 * The bridge, while it stands.
 *
 * A screen for a path that is meant to be taken down, which is why it is built
 * around one number rather than around a workflow: **how much is still coming in
 * by e-mail, and how much of that needed somebody?** The day that first number
 * is zero for a month is the day this screen and the bridge behind it can go.
 *
 * The queue is `outstanding` — the messages from an address nobody has
 * registered, and the ones where nothing could be taken. Everything else is
 * history, and the toggle is the whole of the filtering: an inbox with ten
 * facets is an inbox nobody finishes.
 *
 * **Nothing here approves anything**, and the screen says so above the queue
 * rather than only in the API documentation. An analyst who believed a poll had
 * filed movements would stop looking at the review queue, which is the one place
 * those movements are actually decided.
 *
 * There is no compose box, no reply and no forward. This reads a mailbox; it is
 * not a mail client, and the day it grows a reply field is the day a customer
 * starts being told things that are not the record.
 */
export function MailboxPage() {
  const [onlyOutstanding, setOnlyOutstanding] = useState(true);
  const [messageId, setMessageId] = useState<string | null>(null);
  const [attempt, setAttempt] = useState<number | null>(null);

  const messages = useMailMessages(onlyOutstanding);
  const senders = useMailboxSenders();
  const poll = usePollMailbox();
  const { notify } = useNotifications();

  const columns: readonly Column<MailMessageResponse>[] = [
    {
      key: 'received',
      header: 'Chegou em',
      render: (row) => formatDateTime(row.received_at),
    },
    { key: 'from', header: 'De', render: (row) => row.from_address || <Dash /> },
    {
      key: 'subject',
      header: 'Assunto',
      render: (row) => row.subject ?? <Dash />,
    },
    { key: 'status', header: 'Situação', render: (row) => <StatusBadge status={row.status} /> },
    {
      key: 'attachments',
      header: 'Anexos',
      render: (row) =>
        row.attachment_count === 0 ? (
          <Dash />
        ) : (
          `${formatInteger(row.spreadsheet_count)} planilha(s), ` +
          `${formatInteger(row.document_count)} documento(s)`
        ),
    },
    {
      key: 'open',
      header: 'Ações',
      align: 'end',
      render: (row) => (
        <button
          type="button"
          className="button button--secondary"
          onClick={() => {
            setMessageId(row.id);
            setAttempt(null);
          }}
        >
          {MAILBOX_COPY.open}
        </button>
      ),
    },
  ];

  return (
    <div className="page">
      <PageHeader title={MAILBOX_COPY.title} description={MAILBOX_COPY.description} />

      <Panel title={MAILBOX_COPY.queueTitle} description={MAILBOX_COPY.queueDescription}>
        <p className="panel__footnote panel__footnote--warning">{MAILBOX_COPY.nothingIsApproved}</p>

        <div className="form__actions">
          <button
            type="button"
            className="button"
            disabled={poll.isPending}
            onClick={() =>
              poll.mutate(undefined, {
                onSuccess: (result) =>
                  notify(
                    result.unrouted + result.refused > 0 ? 'warning' : 'success',
                    `${formatInteger(result.fetched)} mensagem(ns) lida(s): ` +
                      `${formatInteger(result.ingested)} processada(s), ` +
                      `${formatInteger(result.unrouted)} sem remetente, ` +
                      `${formatInteger(result.refused)} sem nada aproveitável.`,
                  ),
              })
            }
          >
            {poll.isPending ? MAILBOX_COPY.polling : MAILBOX_COPY.poll}
          </button>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={onlyOutstanding}
              onChange={(event) => setOnlyOutstanding(event.target.checked)}
            />
            {MAILBOX_COPY.onlyOutstanding}
          </label>
        </div>

        {poll.isError && (
          <ErrorState error={poll.error} title="Não foi possível ler a caixa de entrada" />
        )}

        <AsyncSection
          state={messages}
          loadingLabel="Carregando as mensagens…"
          errorTitle="Não foi possível carregar as mensagens"
          isEmpty={(data) => data.items.length === 0}
          emptyTitle="Nenhuma mensagem"
          emptyDescription={onlyOutstanding ? MAILBOX_COPY.noOutstanding : MAILBOX_COPY.noMessages}
        >
          {(data) => (
            <>
              <dl className="definitions">
                <div>
                  <dt>{MAILBOX_COPY.outstanding}</dt>
                  <dd>{formatInteger(data.outstanding_count)}</dd>
                </div>
              </dl>
              <DataTable
                id="mailbox-messages"
                caption="Mensagens lidas da caixa de entrada"
                columns={columns}
                rows={data.items}
                rowKey={(row) => row.id}
                emptyTitle="Nenhuma mensagem"
              />
            </>
          )}
        </AsyncSection>
      </Panel>

      {messageId !== null && (
        <MessagePanel
          messageId={messageId}
          attempt={attempt}
          onAttempt={setAttempt}
          onClose={() => setMessageId(null)}
        />
      )}

      <Panel title={MAILBOX_COPY.sendersTitle} description={MAILBOX_COPY.sendersDescription}>
        <AsyncSection
          state={senders}
          loadingLabel="Carregando os remetentes…"
          errorTitle="Não foi possível carregar os remetentes"
          isEmpty={(data) => data.items.length === 0}
          emptyTitle="Nenhum remetente"
          emptyDescription={MAILBOX_COPY.noSenders}
        >
          {(data) => <SenderTable items={data.items} />}
        </AsyncSection>
      </Panel>
    </div>
  );
}

/**
 * One message, and what became of every file that came with it.
 *
 * `attempt` is why this is a panel rather than a row expansion: reading what we
 * made of a message *before* somebody registered its sender is a second request,
 * and it is the thing an analyst checks when a retry did not do what they
 * expected.
 */
function MessagePanel({
  messageId,
  attempt,
  onAttempt,
  onClose,
}: {
  messageId: string;
  attempt: number | null;
  onAttempt: (attempt: number | null) => void;
  onClose: () => void;
}) {
  const detail = useMailMessage(messageId, attempt);
  const retry = useRetryMailMessage();
  const discard = useDiscardMailMessage();
  const [reason, setReason] = useState('');
  const { notify } = useNotifications();

  return (
    <Panel title={MAILBOX_COPY.messageTitle}>
      <div className="form__actions">
        <button type="button" className="button button--ghost" onClick={onClose}>
          Fechar
        </button>
      </div>

      <AsyncSection
        state={detail}
        loadingLabel="Carregando a mensagem…"
        errorTitle="Não foi possível carregar a mensagem"
      >
        {(data) => (
          <>
            <dl className="definitions">
              <div>
                <dt>De</dt>
                <dd>{data.message.from_address || '—'}</dd>
              </div>
              <div>
                <dt>Assunto</dt>
                <dd>{data.message.subject ?? '—'}</dd>
              </div>
              <div>
                <dt>Chegou em</dt>
                <dd>{formatDateTime(data.message.received_at)}</dd>
              </div>
              <div>
                <dt>Situação</dt>
                <dd>
                  <StatusBadge status={data.message.status} />
                </dd>
              </div>
              <div>
                <dt>{MAILBOX_COPY.attempt}</dt>
                <dd>{formatInteger(data.message.attempt_count)}</dd>
              </div>
            </dl>

            {data.message.refusal_code != null && (
              <p className="panel__footnote panel__footnote--warning">
                {refusalText(data.message.refusal_code, data.message.refusal_reason)}
              </p>
            )}

            {data.message.attempt_count > 1 && (
              <div className="form__actions">
                <button
                  type="button"
                  className="button button--ghost"
                  onClick={() => onAttempt(attempt === null ? 1 : null)}
                >
                  {attempt === null ? MAILBOX_COPY.previousAttempt : MAILBOX_COPY.latestAttempt}
                </button>
              </div>
            )}

            <h3 className="panel__subtitle">{MAILBOX_COPY.attachments}</h3>
            <DataTable
              caption="Anexos desta mensagem"
              columnVisibility={false}
              columns={ATTACHMENT_COLUMNS}
              rows={data.attachments}
              rowKey={(row) => `${row.attempt}-${row.part_number}`}
              emptyTitle="Nenhum anexo"
              emptyDescription={MAILBOX_COPY.noAttachments}
            />

            {isOutstanding(data.message.status) && (
              <>
                <div className="form__actions">
                  <button
                    type="button"
                    className="button"
                    disabled={retry.isPending}
                    onClick={() =>
                      retry.mutate(messageId, {
                        onSuccess: (result) =>
                          notify(
                            result.message.status === MailMessageStatus.INGESTED
                              ? 'success'
                              : 'warning',
                            MAIL_STATUS_LABELS[result.message.status],
                          ),
                      })
                    }
                  >
                    {MAILBOX_COPY.retry}
                  </button>
                </div>
                {retry.isError && (
                  <ErrorState error={retry.error} title="Não foi possível reprocessar a mensagem" />
                )}

                <TextField
                  label={MAILBOX_COPY.discardReason}
                  hint={MAILBOX_COPY.discardHint}
                  value={reason}
                  onChange={setReason}
                />
                <div className="form__actions">
                  <button
                    type="button"
                    className="button button--secondary"
                    disabled={reason.trim() === '' || discard.isPending}
                    onClick={() =>
                      discard.mutate(
                        { messageId, reason: reason.trim() },
                        {
                          onSuccess: () => {
                            setReason('');
                            notify('success', 'Mensagem descartada.');
                          },
                        },
                      )
                    }
                  >
                    {MAILBOX_COPY.discard}
                  </button>
                </div>
                {discard.isError && (
                  <ErrorState error={discard.error} title="Não foi possível descartar a mensagem" />
                )}
              </>
            )}
          </>
        )}
      </AsyncSection>
    </Panel>
  );
}

/**
 * The addresses we trust, and the one action this screen offers over them.
 *
 * Registering one is deliberately **not** here: it is `contract:write`, which an
 * analyst does not hold, and a form that half the people looking at this screen
 * cannot submit is a form that teaches them the screen is broken. Withdrawing is
 * offered because taking trust away is the urgent half.
 */
function SenderTable({ items }: { items: readonly MailboxSenderResponse[] }) {
  const revoke = useRevokeMailboxSender();
  const { notify } = useNotifications();

  const columns: readonly Column<MailboxSenderResponse>[] = [
    { key: 'address', header: 'Endereço', render: (row) => row.address },
    { key: 'label', header: 'Quem é', render: (row) => row.label ?? <Dash /> },
    {
      key: 'kind',
      header: 'Documentos vêm como',
      render: (row) => row.default_document_kind,
    },
    {
      key: 'live',
      header: 'Situação',
      render: (row) =>
        row.is_live ? (
          <span className="badge badge--ok">Autorizado</span>
        ) : (
          <span className="badge">Retirado</span>
        ),
    },
    {
      key: 'revoke',
      header: 'Ações',
      align: 'end',
      render: (row) =>
        row.is_live ? (
          <button
            type="button"
            className="button button--secondary"
            disabled={revoke.isPending}
            onClick={() =>
              revoke.mutate(row.id, {
                onSuccess: () => notify('success', 'Autorização retirada.'),
              })
            }
          >
            {MAILBOX_COPY.revoke}
          </button>
        ) : (
          <Dash />
        ),
    },
  ];

  return (
    <>
      {revoke.isError && (
        <ErrorState error={revoke.error} title="Não foi possível retirar a autorização" />
      )}
      <DataTable
        id="mailbox-senders"
        caption="Endereços autorizados a enviar movimentações"
        columns={columns}
        rows={items}
        rowKey={(row) => row.id}
        emptyTitle="Nenhum remetente"
      />
    </>
  );
}

/**
 * What became of one attached file.
 *
 * A spreadsheet line says "prévia" rather than naming the upload, because that
 * is the fact worth carrying: it is not a movement yet, and it will not be one
 * until somebody confirms it.
 */
const ATTACHMENT_COLUMNS: readonly Column<MailAttachmentResponse>[] = [
  { key: 'file', header: 'Arquivo', render: (row) => row.filename },
  {
    key: 'outcome',
    header: 'O que virou',
    render: (row) => <OutcomeBadge outcome={row.outcome} />,
  },
  {
    key: 'why',
    header: 'Observação',
    render: (row) =>
      row.rejection_code != null ? refusalText(row.rejection_code, row.rejection_reason) : <Dash />,
  },
];

/**
 * The status, coloured by whether it still needs somebody.
 *
 * `ingested` and `discarded` are both closed, so both are quiet — one landed and
 * the other was decided. The other two are the queue.
 */
function StatusBadge({ status }: { status: MailMessageStatus }) {
  const tone =
    status === MailMessageStatus.INGESTED
      ? 'badge--ok'
      : status === MailMessageStatus.DISCARDED
        ? 'badge'
        : 'badge--warning';
  return <span className={`badge ${tone}`}>{MAIL_STATUS_LABELS[status]}</span>;
}

function OutcomeBadge({ outcome }: { outcome: AttachmentOutcome }) {
  const tone =
    outcome === AttachmentOutcome.REJECTED
      ? 'badge--blocking'
      : outcome === AttachmentOutcome.IGNORED
        ? 'badge'
        : 'badge--ok';
  return <span className={`badge ${tone}`}>{ATTACHMENT_OUTCOME_LABELS[outcome]}</span>;
}

function Dash() {
  return <span className="table__muted">—</span>;
}
